import { useTranslation } from '@shared/core';
import { Invitation, Share, Website, MailingAddress, Diamond, Mobile } from '@withjoy/joykit/icons';
import React, { useMemo } from 'react';
import { CustomizeNowButton } from '../../CustomizeNowButton';
import { ListValueProps } from '../ValueProps/ValueProps';
import { MusicNoteIcon } from '../ValueProps/MusicNoteIcon';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { MatchingFoilTemplate } from '../MatchingFoilTemplate';
import { ColorwaySelector } from '../ColorwaySelector';
import { usePdpColorways } from '../../PdpColorwayContext';
import { DigitalValueProps } from '../DigitalValueProps/DigitalValueProps';

type PremiumDigitalCustomizationPaneProps = Readonly<{
  onCustomizeClick: () => void;
  isCreatingDraft: boolean;
  intersectionRef: React.RefObject<HTMLButtonElement>;
  hideCustomizeNowButton?: boolean;
  disableCustomizeNowButton?: boolean;
  category: StationeryTemplateCategoryEnum;
  themeId: string;
  /**
   * Renders the tiered value props shared with Basic Digital V2 instead of the legacy
   * "Premium Digital Perks" list. Driven by the `printEnableBasicDigitalV2` gate.
   * TODO: drop the legacy list and this prop when that flag is retired.
   */
  showDigitalValueProps: boolean;
}>;

// Legacy "Premium Digital Perks" list, shown while the Basic Digital V2 gate is off.
const LegacyPremiumDigitalPerks = () => {
  const { t2, tq } = useTranslation('stationery');
  const { listValueProps } = useMemo(() => {
    const { listValueProps } = t2('pdpModal', 'customizationPane', 'premiumDigitalTemplateConfig');
    const valueProps = {
      listValueProps: {
        sectionTitle: listValueProps.sectionTitle,
        items: [
          {
            icon: (
              <Diamond
                size={24}
                alignSelf="flex-start"
              />
            ),
            title: listValueProps.premium.title,
            description: listValueProps.premium.description,
          },
          {
            icon: (
              <Website
                size={24}
                alignSelf="flex-start"
              />
            ),
            title: listValueProps.matchingPaperCards.title,
            description: listValueProps.matchingPaperCards.description,
          },
          {
            icon: (
              <Invitation
                size={24}
                alignSelf="flex-start"
              />
            ),
            title: listValueProps.personalizedEmail.title,
            description: listValueProps.personalizedEmail.description,
          },
          {
            icon: (
              <Share
                size={24}
                alignSelf="flex-start"
              />
            ),
            title: listValueProps.shareableLink.title,
            description: listValueProps.shareableLink.description,
          },
          {
            icon: (
              <MailingAddress
                size={24}
                alignSelf="flex-start"
              />
            ),
            title: listValueProps.contactCollector.title,
            description: listValueProps.contactCollector.description,
          },
        ],
      },
    };
    valueProps.listValueProps.items.splice(2, 0, {
      icon: (
        <Mobile
          size={24}
          alignSelf="flex-start"
        />
      ),
      title: listValueProps.textMessage.title,
      description: listValueProps.textMessage.description,
    });
    // Second perk, right after Premium Designer.
    valueProps.listValueProps.items.splice(1, 0, {
      icon: <MusicNoteIcon />,
      title: tq`Background Music & Audio`,
      description: tq`Set the perfect mood for your guests with curated background music played right when they open your digital card.`,
    });
    return valueProps;
  }, [t2, tq]);

  return (
    <ListValueProps
      marginBottom={6}
      items={listValueProps.items}
      sectionTitle={listValueProps.sectionTitle}
    />
  );
};

/**
 * Customization pane for the digital products that render from the paper template
 * (Premium Digital and Basic Digital V2).
 */
export const PremiumDigitalCustomizationPane = (props: PremiumDigitalCustomizationPaneProps) => {
  const { disableCustomizeNowButton, onCustomizeClick, hideCustomizeNowButton, isCreatingDraft, intersectionRef, category, themeId, showDigitalValueProps } = props;

  const { options, selectedColorwayId, onColorwaySelect } = usePdpColorways();

  return (
    <>
      <CustomizeNowButton
        ref={intersectionRef}
        hide={hideCustomizeNowButton}
        isLoading={isCreatingDraft}
        onCustomizeClick={onCustomizeClick}
        disabled={disableCustomizeNowButton}
        marginBottom={6}
      />
      <ColorwaySelector
        options={options}
        selectedColorwayId={selectedColorwayId}
        onSelect={onColorwaySelect}
        marginBottom={6}
      />
      {showDigitalValueProps ? <DigitalValueProps marginBottom={6} /> : <LegacyPremiumDigitalPerks />}
      <MatchingFoilTemplate
        category={category}
        themeId={themeId}
      />
    </>
  );
};
